<template>
  <!-- sidebar -->
  <nav class="sidebar bg-white dark:bg-gray-800 overflow-y-auto h-screen">
    <!-- sidebar head -->
    <div class="sidebar-head p-4">
      <router-link
        to="/"
        exact
        class="flex"
      >
        <img
          class="w-8 mt-1 flex-shrink-0"
          src="@/assets/logo/logo.svg"
          alt="logo windzo"
          style="object-fit: contain; height: 32px; width: 32px;"
        />
        <h2
          class="text-2xl font-normal ml-3 mt-2 text-gray-800 dark:text-gray-200"
          translate="no"
        >
          Windzo<span class="text-primary">.</span>
        </h2>
      </router-link>
      <div
        class="bg-gray-700 absolute mt-3 dark:block hidden rounded-md py-1 px-2 text-xs text-gray-200"
      >
        {{ $t('sidebar.darkMode') }}
      </div>
      <button
        class="lg:hidden block dark:text-gray-400 float-right -mt-7"
        @click="$emit('sidebarToggle')"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          role="img"
          width="25px"
          height="25px"
          preserveAspectRatio="xMidYMid meet"
          viewBox="0 0 32 32"
        >
          <path
            fill="currentColor"
            d="M7.219 5.781L5.78 7.22L14.563 16L5.78 24.781l1.44 1.439L16 17.437l8.781 8.782l1.438-1.438L17.437 16l8.782-8.781L24.78 5.78L16 14.563z"
          />
        </svg>
      </button>
    </div>
    <!-- sidebar list -->
    <div class="sidebar-list p-4 mt-4 divide-y dark:divide-gray-700">
      <div class="pb-5">
        <p class="font-medium text-gray-400 dark:text-gray-400">{{ $t('sidebar.menu') }}</p>
        <div class="wrap-item mt-4 dark:text-gray-500">
          <div class="item">
            <router-link
              to="/dashboard"
              exact
              class="w-full flex text-left rounded-md box-border p-3 hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              <span class="mr-3 text-xl"><Icon icon="mdi:view-dashboard" /></span>
              <span class="w-full"> {{ $t('dashboard.title') }} </span>
            </router-link>
          </div>
          <div class="item mt-3">
            <menu-accordion>
              <template v-slot:icon>
                <Icon icon="mdi:office-building" />
              </template>
              <template v-slot:title> {{ $t('sidebar.tenant') }} </template>
              <template v-slot:content>
                <router-link
                  to="/tenant/overview"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:view-dashboard-outline" class="text-base flex-shrink-0" />
                  <span>{{ $t('sidebar.overview') }}</span>
                </router-link>
                <router-link
                  to="/tenant/members"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:account-group-outline" class="text-base flex-shrink-0" />
                  <span>{{ $t('sidebar.member') }}</span>
                </router-link>
                <router-link
                  to="/tenant/agents"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:headset" class="text-base flex-shrink-0" />
                  <span>{{ $t('sidebar.agentManagement') }}</span>
                </router-link>
                <router-link
                  to="/tenant-gateway"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:swap-horizontal" class="text-base flex-shrink-0" />
                  <span>{{ $t('sidebar.switchTenant') }}</span>
                </router-link>
                <!-- Temporarily hidden
                <router-link
                  to="/tenant/settings"
                  @click.stop
                  class="w-full text-left block rounded-md p-3 hover:bg-gray-200 dark:hover:bg-gray-700"
                >
                  Settings
                </router-link>
                -->
              </template>
            </menu-accordion>
          </div>
          <div class="item mt-3">
            <menu-accordion>
              <template v-slot:icon>
                <Icon icon="mdi:robot" />
              </template>
              <template v-slot:title> {{ $t('sidebar.pennyBots') }} </template>
              <template v-slot:content>
                <router-link
                  to="/penny-bots"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:robot-outline" class="text-base flex-shrink-0" />
                  <span>{{ $t('sidebar.bots') }}</span>
                </router-link>
                <router-link
                  to="/penny-connections"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:link-variant" class="text-base flex-shrink-0" />
                  <span>{{ $t('sidebar.connections') }}</span>
                </router-link>
                <router-link
                  to="/penny-rules"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:format-list-checks" class="text-base flex-shrink-0" />
                  <span>{{ $t('sidebar.rules') }}</span>
                </router-link>
                <router-link
                  to="/penny-knowledge-base"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:database-search-outline" class="text-base flex-shrink-0" />
                  <span>{{ $t('sidebar.knowledgeBase') }}</span>
                </router-link>
                <router-link
                  to="/penny-escalation"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:ticket-account" class="text-base flex-shrink-0" />
                  <span>{{ $t('sidebar.escalation') }}</span>
                </router-link>
                <router-link
                  to="/penny-bot-config"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:cog-outline" class="text-base flex-shrink-0" />
                  <span>{{ $t('sidebar.botConfig') }}</span>
                </router-link>
                <hr class="my-1.5 border-gray-200 dark:border-gray-700" />
                <router-link
                  to="/penny/metrics"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:chart-line" class="text-base flex-shrink-0" />
                  <span>{{ $t('sidebar.metrics') }}</span>
                </router-link>
                <router-link
                  to="/penny/monitoring"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:monitor-dashboard" class="text-base flex-shrink-0" />
                  <span>{{ $t('sidebar.monitoring') }}</span>
                </router-link>
                <router-link
                  to="/penny/analytics"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:google-analytics" class="text-base flex-shrink-0" />
                  <span>{{ $t('sidebar.pennyAnalytics') }}</span>
                </router-link>
              </template>
            </menu-accordion>
          </div>
          <div class="item mt-3">
            <menu-accordion>
              <template v-slot:icon>
                <Icon icon="mdi:message-text" />
              </template>
              <template v-slot:title> {{ $t('sidebar.messages') }} </template>
              <template v-slot:content>
                <router-link
                  to="/messages"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:forum-outline" class="text-base flex-shrink-0" />
                  <span>{{ $t('sidebar.conversations') }}</span>
                </router-link>
              </template>
            </menu-accordion>
          </div>
          <div class="item mt-3">
            <menu-accordion>
              <template v-slot:icon>
                <Icon icon="mdi:account-multiple" />
              </template>
              <template v-slot:title> {{ $t('customer.title') }} </template>
              <template v-slot:content>
                <router-link
                  to="/customers"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:account-box-multiple-outline" class="text-base flex-shrink-0" />
                  <span>{{ $t('customers.title') }}</span>
                </router-link>
                <router-link
                  to="/customer/profiles"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:account-card-outline" class="text-base flex-shrink-0" />
                  <span>{{ $t('customer.title') }}</span>
                </router-link>
                <router-link
                  to="/customer/feedback"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:comment-text-outline" class="text-base flex-shrink-0" />
                  <span>{{ $t('feedback.title') }}</span>
                </router-link>
                <router-link
                  to="/customer/analytics"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:chart-box-outline" class="text-base flex-shrink-0" />
                  <span>{{ $t('analytics.title') }}</span>
                </router-link>
                <router-link
                  to="/customer/vip-queue"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:crown" class="text-base flex-shrink-0 text-amber-500" />
                  <span>{{ $t('sidebar.vipQueue') }}</span>
                </router-link>
                <router-link
                  to="/customer/segments"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:chart-pie" class="text-base flex-shrink-0 text-blue-500" />
                  <span>{{ $t('sidebar.segments') }}</span>
                </router-link>
              </template>
            </menu-accordion>
          </div>
          <div class="item mt-3">
            <menu-accordion>
              <template v-slot:icon>
                <Icon icon="mdi:school" />
              </template>
              <template v-slot:title> {{ $t('training.title') }} </template>
              <template v-slot:content>
                <router-link
                  to="/training/library"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:book-open-variant" class="text-base flex-shrink-0" />
                  <span>{{ $t('training.library') }}</span>
                </router-link>
                <router-link
                  to="/training/dashboard"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:view-dashboard-outline" class="text-base flex-shrink-0" />
                  <span>{{ $t('training.agentDashboard') }}</span>
                </router-link>
              </template>
            </menu-accordion>
          </div>
          <div class="item mt-3">
            <menu-accordion>
              <template v-slot:icon>
                <Icon icon="mdi:routes" />
              </template>
              <template v-slot:title> {{ $t('routing.title') }} </template>
              <template v-slot:content>
                <router-link
                  to="/routing/queue"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:view-list-outline" class="text-base flex-shrink-0" />
                  <span>{{ $t('routing.queue.title') }}</span>
                </router-link>
                <router-link
                  to="/routing/supervisor"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:account-eye" class="text-base flex-shrink-0" />
                  <span>{{ $t('routing.supervisor.title') }}</span>
                </router-link>
                <router-link
                  to="/routing/simulation"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:play-circle-outline" class="text-base flex-shrink-0" />
                  <span>{{ $t('routing.simulation.title') }}</span>
                </router-link>
                <router-link
                  to="/routing/escalation"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:arrow-up-bold-box-outline" class="text-base flex-shrink-0" />
                  <span>{{ $t('routing.escalation.title') }}</span>
                </router-link>
              </template>
            </menu-accordion>
          </div>
          <div class="item mt-3" v-if="tenantStore?.currentTenant?.role === 'OWNER'">
            <menu-accordion>
              <template v-slot:icon>
                <Icon icon="mdi:bank-transfer" />
              </template>
              <template v-slot:title> {{ $t('sidebar.payments') }} </template>
              <template v-slot:content>
                <router-link
                  to="/payment/deposit"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:cash-plus" class="text-base flex-shrink-0" />
                  <span>{{ $t('sidebar.deposit') }}</span>
                </router-link>
                <router-link
                  to="/payment/history"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:history" class="text-base flex-shrink-0" />
                  <span>{{ $t('sidebar.history') }}</span>
                </router-link>
              </template>
            </menu-accordion>
          </div>
          <div class="item mt-3" v-if="authStore.isAnyAdmin">
            <menu-accordion>
              <template v-slot:icon>
                <Icon icon="mdi:shield-account" />
              </template>
              <template v-slot:title> {{ $t('sidebar.admin') }} </template>
              <template v-slot:content>
                <router-link
                  to="/admin/bank-account"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:bank-outline" class="text-base flex-shrink-0" />
                  <span>{{ $t('sidebar.bankAccount') }}</span>
                </router-link>
                <router-link
                  to="/admin/packages"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:package-variant-closed" class="text-base flex-shrink-0" />
                  <span>{{ $t('sidebar.packages') }}</span>
                </router-link>
                <router-link
                  to="/admin/discounts"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:ticket-percent-outline" class="text-base flex-shrink-0" />
                  <span>{{ $t('sidebar.discounts') }}</span>
                </router-link>
                <router-link
                  to="/admin/analytics"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:chart-bar" class="text-base flex-shrink-0" />
                  <span>{{ $t('sidebar.analytics') }}</span>
                </router-link>
                <router-link
                  to="/admin/webhooks"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:webhook" class="text-base flex-shrink-0" />
                  <span>{{ $t('sidebar.webhooks') }}</span>
                </router-link>
                <router-link
                  to="/admin/users"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:account-cog-outline" class="text-base flex-shrink-0" />
                  <span>{{ $t('sidebar.userManagement') }}</span>
                </router-link>
                <!-- Conversation Routing & Agent Management -->
                <hr class="my-1.5 border-gray-200 dark:border-gray-700" />
                <router-link
                  to="/admin/skills"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:puzzle-outline" class="text-base flex-shrink-0" />
                  <span>{{ $t('sidebar.skillsManagement') }}</span>
                </router-link>
                <router-link
                  to="/admin/routing-rules"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:routes" class="text-base flex-shrink-0" />
                  <span>{{ $t('sidebar.routingRules') }}</span>
                </router-link>
                <router-link
                  to="/admin/sla-monitoring"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:clock-alert-outline" class="text-base flex-shrink-0" />
                  <span>{{ $t('sidebar.slaMonitoring') }}</span>
                </router-link>
                <router-link
                  to="/admin/escalation-tiers"
                  @click.stop
                  class="w-full text-left block rounded-md p-2.5 px-3 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                >
                  <Icon icon="mdi:format-list-numbered" class="text-base flex-shrink-0" />
                  <span>{{ $t('sidebar.escalationTiers') }}</span>
                </router-link>
              </template>
            </menu-accordion>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>
<script>
  import { Icon } from "@iconify/vue";
  import MenuAccordion from "./MenuAccordion.vue";
  import { useAuthStore } from "@/stores/authStore";
  import { useGatewayTenantStore } from "@/stores/tenant/gateway/myTenantStore";

  export default {
    components: {
      Icon,
      MenuAccordion,
    },
    setup() {
      const authStore = useAuthStore();
      const tenantStore = useGatewayTenantStore();
      return {
        authStore,
        tenantStore,
      };
    },
  };
</script>
